// Admin seat-invoice API + branded invoice document (print-to-PDF).
// Invoice = seats x tier price. Customer downloads, pays via Stripe or check,
// staff mark paid. No auto-charge.
import { supabase } from '@govern360/shared';

// ---- BILLING ENTITY (edit address before sending real invoices) ----
const BILL_FROM = {
  entity: 'Govern360, LLC',
  address: '251 Little Falls Drive, Wilmington, DE 19808',
  email: 'info@aivons.com',
};

export async function staffGenerateSeatInvoice(orgId, periodStart, seats) {
  const { data, error } = await supabase.rpc('staff_generate_seat_invoice', {
    p_org_id: orgId, p_period_start: periodStart,
    p_seats: seats === '' || seats == null ? null : Number(seats),
  });
  return { data, error };
}
export async function staffListSeatInvoices(orgId) {
  const { data, error } = await supabase.rpc('staff_list_seat_invoices', { p_org_id: orgId });
  return { data: data || [], error };
}
export async function staffMarkSeatInvoicePaid(invoiceId, method, reference) {
  const { data, error } = await supabase.rpc('staff_mark_seat_invoice_paid', {
    p_invoice_id: invoiceId, p_method: method, p_reference: reference || null,
  });
  return { data, error };
}
export async function staffVoidSeatInvoice(invoiceId) {
  const { data, error } = await supabase.rpc('staff_void_seat_invoice', { p_invoice_id: invoiceId });
  return { data, error };
}

function money(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Open a branded, printable invoice in a new window (browser print -> PDF).
export function openInvoiceDocument(inv, org) {
  const feats = Array.isArray(inv.features) ? inv.features : [];
  const featRows = feats.map((f) => `<li>${esc(f.feature || f)}</li>`).join('');
  const statusBadge = inv.status === 'paid'
    ? '<span style="color:#137333;border:1px solid #137333;border-radius:4px;padding:2px 8px;font-size:12px">PAID</span>'
    : inv.status === 'void'
      ? '<span style="color:#80868b;border:1px solid #80868b;border-radius:4px;padding:2px 8px;font-size:12px">VOID</span>'
      : '<span style="color:#b06000;border:1px solid #b06000;border-radius:4px;padding:2px 8px;font-size:12px">DUE</span>';

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(inv.invoice_number)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color: #202124; margin: 0; padding: 40px; font-size: 13px; line-height: 1.5; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #5bc4b5; padding-bottom: 20px; margin-bottom: 24px; }
  .brand { display: flex; align-items: center; gap: 12px; }
  .logo { width: 44px; height: 44px; border-radius: 10px; background: #0a0d14; color: #5bc4b5; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 20px; }
  .brand-n { font-size: 20px; font-weight: 700; }
  .brand-s { font-size: 11px; color: #5f6368; }
  .inv-meta { text-align: right; font-size: 12px; color: #5f6368; }
  .inv-meta .num { font-size: 16px; font-weight: 700; color: #202124; }
  .parties { display: flex; justify-content: space-between; margin-bottom: 28px; gap: 40px; }
  .party h4 { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #80868b; margin: 0 0 6px; }
  .party div { font-size: 13px; }
  table.li { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  table.li th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #5f6368; border-bottom: 2px solid #e8eaed; padding: 8px 6px; }
  table.li td { padding: 12px 6px; border-bottom: 1px solid #e8eaed; }
  table.li td.r, table.li th.r { text-align: right; }
  .totals { width: 280px; margin-left: auto; margin-top: 8px; }
  .totals div { display: flex; justify-content: space-between; padding: 6px 6px; }
  .totals .grand { border-top: 2px solid #202124; font-weight: 700; font-size: 16px; padding-top: 10px; }
  .feats { margin-top: 28px; background: #f8f9fa; border-radius: 8px; padding: 16px 20px; }
  .feats h4 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #5f6368; }
  .feats ul { margin: 0; padding-left: 18px; columns: 2; font-size: 12px; color: #202124; }
  .feats li { margin-bottom: 4px; break-inside: avoid; }
  .pay { margin-top: 28px; font-size: 12px; }
  .pay h4 { font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #5f6368; margin: 0 0 6px; }
  .fine { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e8eaed; font-size: 10px; color: #80868b; line-height: 1.6; }
  @media print { body { padding: 0; } .noprint { display: none; } }
</style></head><body>
  <div class="head">
    <div class="brand">
      <div class="logo">G</div>
      <div><div class="brand-n">Govern360</div><div class="brand-s">AI Governance, Security &amp; Compliance</div></div>
    </div>
    <div class="inv-meta">
      <div class="num">${esc(inv.invoice_number)}</div>
      <div>Issued: ${esc((inv.issued_at || '').slice(0,10))}</div>
      <div>Due: ${esc(inv.due_date || '')} (${esc(inv.terms || 'Net 30')})</div>
      <div style="margin-top:6px">${statusBadge}</div>
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <h4>From</h4>
      <div><strong>${esc(BILL_FROM.entity)}</strong></div>
      <div>${esc(BILL_FROM.address)}</div>
      <div>${esc(BILL_FROM.email)}</div>
    </div>
    <div class="party" style="text-align:right">
      <h4>Bill to</h4>
      <div><strong>${esc(inv.bill_to_name || org?.name || '')}</strong></div>
      ${inv.bill_to_address ? `<div>${esc(inv.bill_to_address)}</div>` : ''}
      <div>Billing period: ${esc(inv.period_start)} to ${esc(inv.period_end)}</div>
    </div>
  </div>

  <table class="li">
    <thead><tr><th>Description</th><th class="r">Seats</th><th class="r">Unit / mo</th><th class="r">Amount</th></tr></thead>
    <tbody>
      <tr>
        <td><strong>${esc(inv.tier_name)}</strong> subscription<br><span style="color:#5f6368;font-size:11px">Per-seat monthly plan</span></td>
        <td class="r">${esc(inv.seats)}</td>
        <td class="r">${money(inv.seat_price)}</td>
        <td class="r">${money(inv.subtotal)}</td>
      </tr>
    </tbody>
  </table>

  <div class="totals">
    <div><span>Subtotal</span><span>${money(inv.subtotal)}</span></div>
    <div class="grand"><span>Total due</span><span>${money(inv.total_amount)} ${esc(inv.currency || 'USD')}</span></div>
  </div>

  <div class="feats">
    <h4>Included in ${esc(inv.tier_name)}</h4>
    <ul>${featRows || '<li>See plan details</li>'}</ul>
  </div>

  <div class="pay">
    <h4>Payment</h4>
    <div>Remit by check to ${esc(BILL_FROM.entity)} at the address above, or pay by Stripe using the payment link provided separately. Reference invoice ${esc(inv.invoice_number)} on all payments.</div>
  </div>

  <div class="fine">
    Payment terms: ${esc(inv.terms || 'Net 30')}. Amounts are in ${esc(inv.currency || 'USD')} and exclusive of any applicable taxes.
    This invoice is issued by ${esc(BILL_FROM.entity)}. Questions regarding this invoice should be directed to ${esc(BILL_FROM.email)}.
    Continued use of the service is subject to the applicable order form and master subscription agreement between the parties.
  </div>

  <div class="noprint" style="margin-top:24px;text-align:center">
    <button id="printBtn" style="padding:10px 20px;font-size:14px;border-radius:6px;border:0;background:#5bc4b5;color:#fff;cursor:pointer">Print / Save as PDF</button>
  </div>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Please allow popups for this site to open the invoice.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  // Print is triggered from the PARENT context (CSP in the popup blocks inline
  // onclick handlers). Attach the button handler here and offer auto-print.
  try {
    const btn = w.document.getElementById('printBtn');
    if (btn) btn.addEventListener('click', () => { w.focus(); w.print(); });
  } catch (e) { /* cross-doc timing - ignore */ }
  // Auto-open the print dialog once the document has rendered.
  const doPrint = () => { try { w.focus(); w.print(); } catch (e) { /* user can use the button */ } };
  if (w.document.readyState === 'complete') { setTimeout(doPrint, 300); }
  else { w.onload = () => setTimeout(doPrint, 300); }
}